/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import template from './progressbar.stache';
import DomEventsManager from 'scripts/dom-events-manager';
import datetimeUtil from 'scripts/utils/date-time';
import device from 'scripts/utils/device';

const startYear = 1895;

const ACTIVE_ITEM_CLASS =
    's-history-experience-timeline-historybar-year-selection-list__item--current';

can.Component.extend({
    tag: 'timeline-progressbar',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            isDragged: {
                type: 'boolean',
                value: false,
            },
            yearsWithCards: {
                Type: can.Map,
                Value: can.Map,
            },
            lastYear: {
                get() {
                    return this.attr('yearsWithCards').length
                        ? this.attr('yearsWithCards').slice(-1)[0].year
                        : datetimeUtil.getCurrentYear();
                },
            },
        },
        isCurrentYear(year) {
            return Number(this.attr('currentYear')) === year;
        },
    },
    events: {
        inserted() {
            DomEventsManager.subscribe(this);

            this.viewModel.bind('currentYear', (e, n) => {
                this.handleClass(n);
            });

            this.marker = this.element.find(
                '.s-history-experience-timeline-historybar-progress__marker',
            );
            this.historybar = this.element.find(
                '.s-history-experience-timeline-historybar-progress',
            );

            this.handleMarkerPosition();

            this.registerEventListeners();
            this.handleScroll();
        },

        registerEventListeners() {
            can.$('body').on('click', (event) => {
                this.handleMouseClick(event);
            });
            can.$('body').on('mouseup', () => {
                this.releaseMarker();
            });
            can.$('body').on('mousemove', (event) => {
                this.handleMouseMove(event);
            });
            can.$(window).on('scroll', () => this.handleScroll());
            can.$(window).on('touchmove', () => this.handleScroll());
        },

        storeHistorybarParams() {
            if (this.historybar.length) {
                this.historybarOffsetX = this.historybar[0].getBoundingClientRect().left;
                this.historybarWidth = this.historybar[0].getBoundingClientRect().width;
            }
        },

        handleMarkerPosition() {
            let self = this;
            this.viewModel.bind('currentYear', function(ev, currentYear) {
                if (!self.viewModel.attr('isDragged')) {
                    self.moveMarker(currentYear);
                }
            });
            self.moveMarker(this.viewModel.attr('currentYear'));
        },

        moveMarker(currentYear) {
            this.storeHistorybarParams();
            let markerPositionPercent =
                (100 / (this.viewModel.attr('lastYear') - startYear)) *
                (currentYear - startYear);
            let css =
                markerPositionPercent == 100
                    ? { left: 'auto', right: 0 }
                    : { left: markerPositionPercent + '%', right: 'auto' };
            this.marker.css(css);
        },

        '.s-history-experience-timeline-historybar-year-selection click'(
            element,
        ) {
            this.viewModel.attr(
                'isYearSelectionShown',
                !this.viewModel.attr('isYearSelectionShown'),
            );
        },

        '.s-history-experience-timeline-historybar-year-selection-list__item click'(
            element,
            event,
        ) {
            this.viewModel.attr('currentYear', Number(element.text()));
        },

        '.s-history-experience-timeline-historybar-progress__wrapper, .s-history-experience-timeline-historybar-progress__marker mousedown'(
            element,
            event,
        ) {
            this.grabMarker(event);
        },

        '.s-history-experience-timeline-historybar-progress__wrapper, .s-history-experience-timeline-historybar-progress__marker touchstart'(
            element,
            event,
        ) {
            this.grabMarker(event);
        },

        touchmove(element, event) {
            this.handleMouseMove(event);
        },

        touchend(element, event) {
            this.releaseMarker();
        },

        grabMarker(event) {
            this.viewModel.attr('isDragged', true);
            this.handleMouseMove(event);
        },

        releaseMarker() {
            if (this.viewModel.attr('isDragged')) {
                this.viewModel.attr('isDragged', false);
                this.moveMarker(this.viewModel.attr('currentYear'));
            }
        },

        handleScroll() {
            const offset = device.getIOSBannerOffset();
            const transformEntry = `translate(0, -${offset}px)`;

            can.$('.s-history-experience-timeline-historybar').css({
                transform: transformEntry,
            });
        },

        handleMouseClick(event) {
            if (
                this.viewModel.attr('isYearSelectionShown') &&
                !(
                    event.target.classList.contains(
                        's-history-experience-timeline-historybar-year-selection',
                    ) ||
                    can
                        .$(event.target)
                        .parents(
                            '.s-history-experience-timeline-historybar-year-selection',
                        ).length
                )
            ) {
                this.viewModel.attr('isYearSelectionShown', false);
            }
        },

        handleMouseMove(event) {
            if (this.viewModel.attr('isDragged')) {
                event.stopPropagation();
                event.preventDefault();
                event.cancelBubble = true;
                event.returnValue = false;

                let eventX =
                    event.clientX || event.originalEvent.touches[0].clientX;
                let mousePosition = eventX - this.historybarOffsetX;
                let positionCss = {};
                if (mousePosition < 0) {
                    positionCss.left = 0;
                    positionCss.right = 'auto';
                } else if (mousePosition > this.historybarWidth - 4) {
                    positionCss.left = 'auto';
                    positionCss.right = 0;
                } else {
                    positionCss.left = mousePosition;
                    positionCss.right = 'auto';
                }
                this.marker.css(positionCss);

                let pixelsPerYear =
                    this.historybarWidth /
                    (this.viewModel.attr('lastYear') - startYear);
                let clickedYear =
                    Math.floor(mousePosition / pixelsPerYear) + startYear;
                let years = this.viewModel.attr('yearsWithCards').serialize();
                let currentYear = years.find((year, index) => {
                    let nextYear = years[index + 1]
                        ? years[index + 1].year
                        : null;
                    return (
                        year.year <= clickedYear &&
                        (!nextYear || nextYear > clickedYear)
                    );
                });

                if (!currentYear) {
                    currentYear = years[0].year;
                } else {
                    currentYear = currentYear.year;
                }

                this.viewModel.attr('currentYear', Number(currentYear));
            }
        },

        onWindowResize() {
            this.storeHistorybarParams();
            this.handleScroll();
        },

        handleClass(n) {
            this.element
                .find(
                    '.s-history-experience-timeline-historybar-year-selection-list__item',
                )
                .removeClass(ACTIVE_ITEM_CLASS);
            this.element.find(`*[data-year=${n}]`).addClass(ACTIVE_ITEM_CLASS);
        },
    },
});
